import {
    AgGridEvent,
    ClientSideRowModelModule,
    GridApi,
    ColumnResizedEvent,
    FilterChangedEvent,
    GridReadyEvent,
    SelectionChangedEvent,
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { TachoReleaseInformationModel } from '../../../../core/domainModels/versions/TachoInstallerModel';
import { versionsTableDefaultColumnsDefs, versionsTableColumnsDefinitions } from '../models/columnDefinitions';
import { VersionsDetails } from './VersionsDetails';
import { TableButtonRenderer } from '../../../components/AgGrid/Renderers/TableRemoveButton/TableRemoveButton';
import { TachoModal } from '../../../components/TachoModal';
import { TachoEditModalTitle } from '../../../components/TachoModal/TachoEditTitle';
import { Icon } from 'tacho-ui';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../../../state/ApplicationState';
import {
    applyInitialSortFromDisplayPreferences,
    createTachoColumnState,
} from '../../../../core/domainModels/displayPreferences/tachoColumnState';
import { modifyVersionsDisplayPreferences, modifyVersionsTableFilters } from '../../../../state/versions/reducer';
import AgGridContextMenuWrapper from '../../../components/ContextMenu/AgGridContextMenuWrapper';

interface VersionsTableProps {
    installers: Array<TachoReleaseInformationModel>;
    readOnly?: boolean;
    onInstallerDoubleClicked: () => void;
    onSelectionChanged?: (companies: Array<TachoReleaseInformationModel>) => void;
}
export const VersionsTable = (props: VersionsTableProps) => {
    const { formatMessage } = useIntl();
    const dispatch = useDispatch();
    const gridApi = useRef<GridApi>();
    const [detailsModalData, setDetailsModalData] = useState<{ modalOpen: boolean; data?: TachoReleaseInformationModel }>({
        modalOpen: false,
        data: undefined,
    });
    const displayPreferences = useSelector((state: ApplicationState) => state.versions.displayPreferences);
    const tableFiltersState = useSelector((state: ApplicationState) => state.versions.tableFilters);

    const openVersionDetailsModal = useCallback((isModalOpen: boolean, data?: TachoReleaseInformationModel) => {
        setDetailsModalData({ modalOpen: isModalOpen, data: data });
    }, []);

    const columnDefs = useMemo(
        () => versionsTableColumnsDefinitions(formatMessage, openVersionDetailsModal, props.readOnly, displayPreferences),
        [formatMessage, openVersionDetailsModal, props.readOnly, displayPreferences],
    );

    const rowsSelectionChanged = (event: SelectionChangedEvent) => {
        const selectedNodes = event.api.getSelectedNodes()?.map(node => node.data as TachoReleaseInformationModel);
        if (!selectedNodes) {
            console.log('No selected rows.');
            return;
        }
        props.onSelectionChanged?.(selectedNodes);
    };

    const onGridReady = useCallback(
        (params: GridReadyEvent) => {
            gridApi.current = params.api;
            applyInitialSortFromDisplayPreferences(params, displayPreferences, [{ colId: 'version', sort: 'asc' }]);
            params.api.setFilterModel(tableFiltersState);
            params.api.setGridOption('rowData', props.installers);
        },
        [props.installers, displayPreferences, tableFiltersState],
    );
    function onColumnsChanged(event: AgGridEvent): void {
        const tachoColumnState = createTachoColumnState(event.api.getColumnState());
        dispatch(modifyVersionsDisplayPreferences(tachoColumnState));
    }
    const onColumnResized = (event: ColumnResizedEvent) => {
        if (event.finished && event.source === 'uiColumnResized') {
            return onColumnsChanged(event);
        }
    };
    const onTableFiltersStateChanged = (event: FilterChangedEvent) => {
        const filters = event.api.getFilterModel();
        dispatch(modifyVersionsTableFilters(filters));
    };
    return (
        <>
            <AgGridContextMenuWrapper gridApi={gridApi.current} showStatusBar>
                <AgGridReact
                    suppressPropertyNamesCheck
                    getRowId={params => params.data?.id ?? ''}
                    suppressDragLeaveHidesColumns
                    suppressContextMenu={true}
                    multiSortKey="ctrl"
                    rowSelection="multiple"
                    defaultColDef={versionsTableDefaultColumnsDefs}
                    columnDefs={columnDefs}
                    components={{ tableButtonRenderer: TableButtonRenderer }}
                    modules={[ClientSideRowModelModule]}
                    onGridReady={onGridReady}
                    onSelectionChanged={rowsSelectionChanged}
                    suppressScrollOnNewData={true}
                    onRowDoubleClicked={params => props.onInstallerDoubleClicked()}
                    onSortChanged={event => onColumnsChanged(event)}
                    onColumnMoved={event => onColumnsChanged(event)}
                    onColumnResized={event => onColumnResized(event)}
                    onFilterChanged={event => onTableFiltersStateChanged(event)}
                />
            </AgGridContextMenuWrapper>

            <>
                {detailsModalData.modalOpen && (
                    <TachoModal
                        height="auto"
                        size="md"
                        isOpen={!!detailsModalData}
                        title={<TachoEditModalTitle icon={Icon.SettingsVersions} titleToTranslation="versionsPatchNotes" />}
                        hideSubmitButton
                        cancelLabelToTranslate="tachoModalClose"
                        onToggle={() => setDetailsModalData({ data: undefined, modalOpen: false })}
                    >
                        <VersionsDetails data={detailsModalData.data} />
                    </TachoModal>
                )}
            </>
        </>
    );
};
